import React, { useEffect, useState } from "react";
import { api, apiErr } from "../lib/api";
import { Button } from "./ui/button";
import { Input, Label } from "./ui/input";
import { Badge } from "./ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { ShieldCheck, ShieldOff, KeyRound, Download, Smartphone, Laptop, Trash2, Building2 } from "lucide-react";
import { toast } from "sonner";

function BackupCodes({ codes, filename }) {
  const download = () => {
    const blob = new Blob([`Lestari ESG — Two-factor backup codes\nEach code works once. Store securely.\n\n${codes.join("\n")}\n`], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const el = document.createElement("a");
    el.href = url; el.download = filename; el.click();
    URL.revokeObjectURL(url);
  };
  return (
    <div className="border border-amber-500/40 bg-amber-500/10 rounded-sm p-4" data-testid="backup-codes-box">
      <div className="text-xs font-semibold text-amber-500 mb-2">Backup codes — shown only once. Each works a single time.</div>
      <div className="grid grid-cols-2 gap-1.5 font-mono text-sm" data-testid="backup-codes-list">
        {codes.map((c) => <div key={c}>{c}</div>)}
      </div>
      <Button size="sm" variant="outline" className="mt-3" onClick={download} data-testid="download-backup-codes">
        <Download className="h-3.5 w-3.5 mr-1" /> Download .txt
      </Button>
    </div>
  );
}

function deviceLabel(ua) {
  if (/iphone|ipad/i.test(ua)) return "iPhone / iPad";
  if (/android/i.test(ua)) return "Android device";
  if (/macintosh/i.test(ua)) return "Mac";
  if (/windows/i.test(ua)) return "Windows PC";
  if (/linux/i.test(ua)) return "Linux";
  return "Unknown device";
}

function TrustedDevices() {
  const [devices, setDevices] = useState([]);
  const load = () => api.get("/auth/mfa/trusted-devices").then((r) => setDevices(r.data.devices)).catch(() => {});
  useEffect(() => { load(); }, []);
  const revoke = async (id) => {
    try { await api.delete(`/auth/mfa/trusted-devices/${id}`); toast.success("Device revoked — it will need a code at next sign-in"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const revokeAll = async () => {
    try { await api.delete("/auth/mfa/trusted-devices"); toast.success("All trusted devices revoked"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <div className="border-t border-border pt-4 mt-4" data-testid="trusted-devices-section">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
        <div className="text-sm font-semibold flex items-center gap-2"><Laptop className="h-4 w-4 text-accent" /> Trusted devices</div>
        {devices.length > 0 && (
          <Button variant="outline" size="sm" onClick={revokeAll} data-testid="revoke-all-devices-btn">Revoke all</Button>
        )}
      </div>
      {devices.length === 0 ? (
        <p className="text-xs text-muted-foreground" data-testid="trusted-devices-empty">
          No trusted devices — a verification code is required at every sign-in. Tick "Remember this device"
          during sign-in to skip the code on that device for 30 days.
        </p>
      ) : (
        <div className="space-y-2">
          {devices.map((d) => (
            <div key={d.id} className="flex items-center justify-between gap-3 text-xs border border-border rounded-sm px-3 py-2" data-testid={`trusted-device-${d.id}`}>
              <div>
                <div className="font-medium">{deviceLabel(d.label)}</div>
                <div className="text-muted-foreground">
                  Trusted until {new Date(d.expires_at).toLocaleDateString("en-SG", { day: "numeric", month: "short", year: "numeric" })}
                  {d.last_used_at && <> · last used {new Date(d.last_used_at).toLocaleDateString("en-SG", { day: "numeric", month: "short" })}</>}
                </div>
              </div>
              <Button variant="ghost" size="icon" onClick={() => revoke(d.id)} data-testid={`revoke-device-${d.id}`}>
                <Trash2 className="h-3.5 w-3.5 text-red-400" />
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export const MfaPolicyCard = () => {
  const [pol, setPol] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/auth/mfa/policy").then((r) => setPol(r.data)).catch(() => {}); }, []);
  if (!pol?.can_manage) return null;
  const toggle = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/auth/mfa/policy", { required: !pol.required });
      setPol({ ...pol, required: data.required });
      toast.success(data.required
        ? "Two-factor authentication is now required for everyone in your organisation"
        : "Organisation-wide two-factor requirement removed");
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  return (
    <div className="card-flat p-6 max-w-2xl" data-testid="mfa-policy-card">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className={`h-10 w-10 rounded-sm flex items-center justify-center ${pol.required ? "bg-accent/15 text-accent" : "bg-surface-hover text-muted-foreground"}`}>
            <Building2 className="h-5 w-5" />
          </div>
          <div>
            <h2 className="font-display font-semibold text-lg">Organisation policy — require 2FA</h2>
            <p className="text-xs text-muted-foreground">
              When on, every user in your organisation must set up an authenticator app before using Lestari.
            </p>
          </div>
        </div>
        <Badge variant={pol.required ? "success" : "outline"} className="uppercase text-[10px]" data-testid="mfa-policy-badge">
          {pol.required ? "Required" : "Optional"}
        </Badge>
      </div>
      <div className="mt-4">
        <Button variant={pol.required ? "destructive" : "primary"} size="sm" onClick={toggle} disabled={busy} data-testid="mfa-policy-toggle">
          {busy ? "Saving…" : pol.required ? "Stop requiring 2FA" : "Require 2FA for all users"}
        </Button>
      </div>
    </div>
  );
};

export const SecurityMfa = ({ onStatusChange }) => {
  const [st, setSt] = useState(null);
  const [busy, setBusy] = useState(false);
  const [enrollOpen, setEnrollOpen] = useState(false);
  const [setup, setSetup] = useState(null);
  const [code, setCode] = useState("");
  const [newCodes, setNewCodes] = useState(null);
  const [disableOpen, setDisableOpen] = useState(false);
  const [regenOpen, setRegenOpen] = useState(false);

  const load = () => api.get("/auth/mfa/status").then((r) => { setSt(r.data); onStatusChange?.(r.data); }).catch(() => {});
  useEffect(() => { load(); }, []);

  const startEnroll = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/mfa/setup");
      setSetup(data); setCode(""); setNewCodes(null); setEnrollOpen(true);
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  const confirmEnable = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/mfa/enable", { code: code.trim() });
      setNewCodes(data.backup_codes);
      toast.success("Two-factor authentication enabled");
      load();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  const disable = async () => {
    setBusy(true);
    try {
      await api.post("/auth/mfa/disable", { code: code.trim() });
      toast.success("Two-factor authentication disabled");
      setDisableOpen(false); setCode(""); load();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  const regenerate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/mfa/backup-codes/regenerate", { code: code.trim() });
      setNewCodes(data.backup_codes);
      toast.success("New backup codes generated — previous codes are invalid");
      load();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  if (!st) return <div className="card-flat p-6 text-sm text-muted-foreground">Loading…</div>;

  return (
    <div className="card-flat p-6 max-w-2xl" data-testid="security-mfa-card">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className={`h-10 w-10 rounded-sm flex items-center justify-center ${st.enabled ? "bg-accent/15 text-accent" : "bg-surface-hover text-muted-foreground"}`}>
            {st.enabled ? <ShieldCheck className="h-5 w-5" /> : <ShieldOff className="h-5 w-5" />}
          </div>
          <div>
            <h2 className="font-display font-semibold text-lg">Two-factor authentication (TOTP)</h2>
            <p className="text-xs text-muted-foreground">Works with Google Authenticator, Microsoft Authenticator, 1Password and any TOTP app.</p>
          </div>
        </div>
        <Badge variant={st.enabled ? "success" : "outline"} className="uppercase text-[10px]" data-testid="mfa-status-badge">
          {st.enabled ? "Enabled" : "Disabled"}
        </Badge>
      </div>

      {!st.enabled ? (
        <div className="mt-5">
          <p className="text-sm text-muted-foreground mb-3">Add a second sign-in step: after your password, you'll enter a 6-digit code from your authenticator app.</p>
          <Button variant="primary" onClick={startEnroll} disabled={busy} data-testid="enable-mfa-btn">
            <Smartphone className="h-4 w-4 mr-1.5" /> Enable two-factor authentication
          </Button>
        </div>
      ) : (
        <div className="mt-5 space-y-3">
          <div className="text-sm">Backup codes remaining: <span className="font-mono font-semibold" data-testid="backup-codes-remaining">{st.backup_codes_remaining}</span></div>
          {newCodes && <BackupCodes codes={newCodes} filename="lestari-backup-codes.txt" />}
          <div className="flex gap-2 flex-wrap">
            <Button variant="outline" size="sm" onClick={() => { setCode(""); setNewCodes(null); setRegenOpen(true); }} data-testid="regen-codes-btn">
              <KeyRound className="h-3.5 w-3.5 mr-1" /> Regenerate backup codes
            </Button>
            <Button variant="destructive" size="sm" onClick={() => { setCode(""); setDisableOpen(true); }} data-testid="disable-mfa-btn">
              <ShieldOff className="h-3.5 w-3.5 mr-1" /> Disable 2FA
            </Button>
          </div>
          <TrustedDevices />
        </div>
      )}

      {/* Enrollment dialog */}
      <Dialog open={enrollOpen} onOpenChange={(o) => { setEnrollOpen(o); if (!o) load(); }}>
        <DialogContent className="max-w-md" data-testid="mfa-enroll-dialog">
          <DialogHeader><DialogTitle>Set up authenticator app</DialogTitle></DialogHeader>
          {setup && !newCodes && (
            <div className="space-y-4">
              <ol className="text-xs text-muted-foreground list-decimal ml-4 space-y-1">
                <li>Open Google Authenticator, Microsoft Authenticator or 1Password</li>
                <li>Scan this QR code (or enter the key manually)</li>
                <li>Enter the 6-digit code shown by the app</li>
              </ol>
              <div className="flex justify-center bg-white rounded-sm p-3">
                <img src={setup.qr_png} alt="TOTP QR code" className="h-44 w-44" data-testid="mfa-qr" />
              </div>
              <div>
                <div className="label-eyebrow text-[10px] mb-1">Manual entry key</div>
                <div className="font-mono text-xs bg-background border border-border rounded-sm p-2 break-all" data-testid="mfa-manual-key">{setup.secret}</div>
              </div>
              <div>
                <Label htmlFor="enroll-code">6-digit code</Label>
                <Input id="enroll-code" value={code} onChange={(e) => setCode(e.target.value)} inputMode="numeric" placeholder="123456" data-testid="mfa-enroll-code" />
              </div>
              <Button variant="primary" className="w-full" onClick={confirmEnable} disabled={busy || code.trim().length < 6} data-testid="mfa-enroll-confirm">
                {busy ? "Verifying…" : "Verify & enable"}
              </Button>
            </div>
          )}
          {newCodes && (
            <div className="space-y-4">
              <BackupCodes codes={newCodes} filename="lestari-backup-codes.txt" />
              <Button variant="primary" className="w-full" onClick={() => { setEnrollOpen(false); setNewCodes(null); load(); }} data-testid="mfa-enroll-done">
                I have saved my backup codes
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Disable dialog */}
      <Dialog open={disableOpen} onOpenChange={setDisableOpen}>
        <DialogContent className="max-w-sm" data-testid="mfa-disable-dialog">
          <DialogHeader><DialogTitle>Disable two-factor authentication</DialogTitle></DialogHeader>
          <p className="text-xs text-muted-foreground">Enter a current authenticator code (or an unused backup code) to confirm.</p>
          <Input value={code} onChange={(e) => setCode(e.target.value)} placeholder="123456 or XXXX-XXXX" data-testid="mfa-disable-code" />
          <Button variant="destructive" className="w-full" onClick={disable} disabled={busy || !code.trim()} data-testid="mfa-disable-confirm">
            {busy ? "Disabling…" : "Disable 2FA"}
          </Button>
        </DialogContent>
      </Dialog>

      {/* Regenerate dialog */}
      <Dialog open={regenOpen} onOpenChange={(o) => { setRegenOpen(o); if (!o) setCode(""); }}>
        <DialogContent className="max-w-sm" data-testid="mfa-regen-dialog">
          <DialogHeader><DialogTitle>Regenerate backup codes</DialogTitle></DialogHeader>
          {!newCodes ? (
            <>
              <p className="text-xs text-muted-foreground">Enter a current authenticator code to generate a fresh set. Old codes stop working immediately.</p>
              <Input value={code} onChange={(e) => setCode(e.target.value)} inputMode="numeric" placeholder="123456" data-testid="mfa-regen-code" />
              <Button variant="primary" className="w-full" onClick={regenerate} disabled={busy || code.trim().length < 6} data-testid="mfa-regen-confirm">
                {busy ? "Generating…" : "Generate new codes"}
              </Button>
            </>
          ) : (
            <>
              <BackupCodes codes={newCodes} filename="lestari-backup-codes.txt" />
              <Button variant="primary" className="w-full" onClick={() => { setRegenOpen(false); setNewCodes(null); }} data-testid="mfa-regen-done">Done</Button>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
